import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useOutletContext, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import { axiosPrivate } from '../../../../api/axios';
import CommonAgGrid from '../../../common/DataGrid/CommonAgGrid';
import PresetList from './PresetList';
import PresetDialog from './PresetDialog';
import SceneDialog from './SceneDialog';

/**
 * 카메라 / 씬플레이 프리셋 관리 페이지
 *
 * 기존에 파일(CameraSet.json, ScenePlay.json)로 관리하던 카메라와 씬을 DB 프리셋 단위로 편집합니다.
 * 카메라·씬 저장은 diff 없이 "현재 화면의 전체 목록으로 교체"입니다(행 수가 적어 이 편이 단순하고 안전).
 *
 * 화면에서 다루는 카메라 번호는 CameraSet.json 의 No(0-base)와 같습니다.
 * ScenePlay.json 의 Cam(1-base) 변환은 서버가 응답을 만들 때만 수행합니다.
 */

const NUMERIC = { type: 'numericColumn', cellStyle: { textAlign: 'center' } };

// 셀 편집 결과는 문자열이라 숫자 컬럼은 명시적으로 변환합니다.
const numberParser = (params) => {
  const parsed = Number(params.newValue);
  return Number.isFinite(parsed) ? parsed : 0;
};
const intParser = (params) => {
  const parsed = Math.trunc(Number(params.newValue));
  return Number.isFinite(parsed) ? parsed : 0;
};
const floatFormatter = (params) =>
  params.value === null || params.value === undefined ? '' : Number(params.value).toFixed(4);

let uidSeq = 0;
const nextUid = () => {
  uidSeq += 1;
  return `row-${uidSeq}`;
};

const ViewPreset = () => {
  const theme = useTheme();
  const location = useLocation();
  const { group_code, project_code } = useParams();
  const { projectName } = useOutletContext();

  const cameraGridRef = useRef();
  const stepGridRef = useRef();

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const projectId = queryParams.get('pid');
  const folderType = (queryParams.get('folder') || 'dt').toLowerCase() === 'sv' ? 'sv' : 'dt';
  const featureName = folderType === 'sv' ? 'SimView' : '디지털트윈';

  const [presets, setPresets] = useState([]);
  const [myDefaultPresetId, setMyDefaultPresetId] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState(null);
  const [loadError, setLoadError] = useState('');

  const [tab, setTab] = useState(0);

  const [cameras, setCameras] = useState([]);
  const [camerasDirty, setCamerasDirty] = useState(false);
  const [selectedCameras, setSelectedCameras] = useState([]);

  const [scenes, setScenes] = useState([]);
  const [scenesDirty, setScenesDirty] = useState(false);
  const [selectedSceneNo, setSelectedSceneNo] = useState(null);
  const [selectedSteps, setSelectedSteps] = useState([]);

  const [presetDialog, setPresetDialog] = useState({ open: false, mode: 'add' });
  const [sceneDialog, setSceneDialog] = useState({ open: false, mode: 'add' });

  const projectBody = useMemo(
    () => ({
      project_id: projectId,
      group_code,
      project_code,
      folder_type: folderType,
    }),
    [projectId, group_code, project_code, folderType]
  );

  const selectedPreset = presets.find((p) => p.presetId === selectedPresetId) || null;
  const selectedScene = scenes.find((s) => s.sceneNo === selectedSceneNo) || null;

  const apiMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

  /* ------------------------------------------------------------ 목록 로드 */

  const fetchPresets = useCallback(
    async (keepPresetId) => {
      if (!projectId) return;
      try {
        const res = await axiosPrivate.post('/view/presets/list', projectBody);
        const list = res.data.presets || [];
        setPresets(list);
        setMyDefaultPresetId(res.data.myDefaultPresetId ?? null);
        setCanEdit(Boolean(res.data.canEdit));
        setLoadError('');

        setSelectedPresetId((current) => {
          const wanted = keepPresetId ?? current;
          if (wanted && list.some((p) => p.presetId === wanted)) return wanted;
          const projectDefault = list.find((p) => p.isDefault === 1);
          return projectDefault ? projectDefault.presetId : list[0]?.presetId ?? null;
        });
      } catch (err) {
        setPresets([]);
        setLoadError(apiMessage(err, '프리셋 목록을 불러오지 못했습니다.'));
      }
    },
    [projectId, projectBody]
  );

  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);

  const fetchPresetContents = useCallback(
    async (presetId) => {
      if (!presetId) {
        setCameras([]);
        setScenes([]);
        setSelectedSceneNo(null);
        return;
      }
      try {
        const [cameraRes, sceneRes] = await Promise.all([
          axiosPrivate.post('/view/cameras/list', { ...projectBody, presetId }),
          axiosPrivate.post('/view/scenes/list', { ...projectBody, presetId }),
        ]);

        setCameras((cameraRes.data.cameras || []).map((c) => ({ ...c, _uid: nextUid() })));
        setCamerasDirty(false);
        setSelectedCameras([]);

        const loadedScenes = (sceneRes.data.scenes || []).map((s) => ({
          ...s,
          steps: (s.steps || []).map((step) => ({ ...step, _uid: nextUid() })),
        }));
        setScenes(loadedScenes);
        setScenesDirty(false);
        setSelectedSteps([]);
        setSelectedSceneNo(loadedScenes[0]?.sceneNo ?? null);
      } catch (err) {
        alert(apiMessage(err, '프리셋 내용을 불러오지 못했습니다.'));
      }
    },
    [projectBody]
  );

  useEffect(() => {
    fetchPresetContents(selectedPresetId);
  }, [selectedPresetId, fetchPresetContents]);

  /** 저장하지 않은 변경이 있으면 되돌아갈지 확인합니다. */
  const confirmDiscard = () => {
    if (!camerasDirty && !scenesDirty) return true;
    return window.confirm('저장하지 않은 변경 내용이 있습니다. 버리고 이동할까요?');
  };

  const handleSelectPreset = (presetId) => {
    if (presetId === selectedPresetId) return;
    if (!confirmDiscard()) return;
    setSelectedPresetId(presetId);
  };

  /* ------------------------------------------------------------ 프리셋 조작 */

  const handleSavePreset = async ({ presetName, description, sourcePresetId }) => {
    try {
      if (presetDialog.mode === 'add') {
        const res = await axiosPrivate.post('/view/presets/create', {
          ...projectBody,
          presetName,
          description,
          sourcePresetId,
        });
        setPresetDialog({ open: false, mode: 'add' });
        await fetchPresets(res.data.presetId);
      } else {
        await axiosPrivate.post('/view/presets/update', {
          ...projectBody,
          presetId: selectedPresetId,
          presetName,
          description,
          isLocked: selectedPreset?.isLocked ?? 0,
        });
        setPresetDialog({ open: false, mode: 'add' });
        await fetchPresets(selectedPresetId);
      }
    } catch (err) {
      alert(apiMessage(err, '프리셋을 저장하지 못했습니다.'));
    }
  };

  const handleDeletePreset = async () => {
    if (!selectedPreset) return;
    if (!window.confirm(`"${selectedPreset.presetName}" 프리셋을 삭제할까요? 카메라와 씬도 함께 삭제됩니다.`)) return;
    try {
      await axiosPrivate.post('/view/presets/delete', { ...projectBody, presetId: selectedPresetId });
      await fetchPresets(null);
    } catch (err) {
      alert(apiMessage(err, '프리셋을 삭제하지 못했습니다.'));
    }
  };

  const handleSetProjectDefault = async () => {
    try {
      await axiosPrivate.post('/view/presets/setProjectDefault', {
        ...projectBody,
        presetId: selectedPresetId,
      });
      await fetchPresets(selectedPresetId);
    } catch (err) {
      alert(apiMessage(err, '기본 프리셋을 지정하지 못했습니다.'));
    }
  };

  const handleSetMyDefault = async (presetId) => {
    try {
      await axiosPrivate.post('/view/presets/setMyDefault', { ...projectBody, presetId });
      await fetchPresets(selectedPresetId);
    } catch (err) {
      alert(apiMessage(err, '내 기본 프리셋을 저장하지 못했습니다.'));
    }
  };

  /* -------------------------------------------------------------- 카메라 */

  const cameraColumnDefs = useMemo(
    () => [
      {
        field: 'camNo',
        headerName: '번호',
        headerTooltip: 'CameraSet.json 의 No 입니다. (ScenePlay.json 의 Cam = No + 1)',
        width: 90,
        editable: false,
        ...NUMERIC,
      },
      { field: 'camName', headerName: '이름', width: 140, editable: canEdit },
      { field: 'camType', headerName: '타입', width: 90, editable: canEdit, valueParser: intParser, ...NUMERIC },
      { field: 'px', headerName: 'Px', width: 120, editable: canEdit, valueParser: numberParser, valueFormatter: floatFormatter, ...NUMERIC },
      { field: 'py', headerName: 'Py', width: 120, editable: canEdit, valueParser: numberParser, valueFormatter: floatFormatter, ...NUMERIC },
      { field: 'pz', headerName: 'Pz', width: 120, editable: canEdit, valueParser: numberParser, valueFormatter: floatFormatter, ...NUMERIC },
      { field: 'ax', headerName: 'Ax', width: 120, editable: canEdit, valueParser: numberParser, valueFormatter: floatFormatter, ...NUMERIC },
      { field: 'ay', headerName: 'Ay', width: 120, editable: canEdit, valueParser: numberParser, valueFormatter: floatFormatter, ...NUMERIC },
      { field: 'az', headerName: 'Az', width: 120, editable: canEdit, valueParser: numberParser, valueFormatter: floatFormatter, ...NUMERIC },
    ],
    [canEdit]
  );

  const handleAddCamera = () => {
    const nextNo = cameras.reduce((max, c) => Math.max(max, c.camNo), -1) + 1;
    setCameras((prev) => [
      ...prev,
      {
        _uid: nextUid(),
        camNo: nextNo,
        camName: String(nextNo + 1),
        camType: 0,
        px: 0, py: 0, pz: 0, ax: 0, ay: 0, az: 0,
      },
    ]);
    setCamerasDirty(true);
  };

  const handleDeleteCameras = () => {
    if (selectedCameras.length === 0) return;
    const removing = new Set(selectedCameras.map((c) => c.camNo));
    // 씬에서 사용 중인 카메라는 서버도 거부하므로 여기서 먼저 막습니다.
    const inUse = [];
    for (const scene of scenes) {
      for (const step of scene.steps) {
        if (removing.has(step.camNo)) inUse.push(`${scene.sceneName}(No.${step.camNo})`);
      }
    }
    if (inUse.length > 0) {
      alert(`씬에서 사용 중인 카메라는 삭제할 수 없습니다:\n${[...new Set(inUse)].join(', ')}`);
      return;
    }
    setCameras((prev) => prev.filter((c) => !removing.has(c.camNo)));
    setSelectedCameras([]);
    setCamerasDirty(true);
  };

  const handleSaveCameras = async () => {
    try {
      await axiosPrivate.post('/view/cameras/save', {
        ...projectBody,
        presetId: selectedPresetId,
        cameras: cameras.map(({ _uid, ...rest }) => rest),
      });
      await fetchPresetContents(selectedPresetId);
      await fetchPresets(selectedPresetId);
      alert('카메라를 저장했습니다.');
    } catch (err) {
      alert(apiMessage(err, '카메라를 저장하지 못했습니다.'));
    }
  };

  /* ------------------------------------------------------------------ 씬 */

  const cameraLabel = useCallback(
    (camNo) => {
      const camera = cameras.find((c) => c.camNo === camNo);
      if (!camera) return `${camNo} (없음)`;
      return camera.camName ? `${camNo} · ${camera.camName}` : String(camNo);
    },
    [cameras]
  );

  const stepColumnDefs = useMemo(
    () => [
      {
        headerName: '순서',
        width: 80,
        editable: false,
        valueGetter: (params) => (params.node?.rowIndex ?? 0) + 1,
        ...NUMERIC,
      },
      {
        field: 'camNo',
        headerName: '카메라',
        width: 180,
        editable: canEdit,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: cameras.map((c) => c.camNo) },
        valueFormatter: (params) => cameraLabel(params.value),
        cellStyle: { textAlign: 'center' },
      },
      { field: 'moveTime', headerName: '이동(초)', width: 110, editable: canEdit, valueParser: numberParser, ...NUMERIC },
      { field: 'waitTime', headerName: '대기(초)', width: 110, editable: canEdit, valueParser: numberParser, ...NUMERIC },
      { field: 'flr', headerName: '층 표시(Flr)', width: 150, editable: canEdit },
      { field: 'grp', headerName: '그룹 표시(Grp)', width: 150, editable: canEdit },
      { field: 'viewPoint', headerName: '시점', width: 90, editable: canEdit, valueParser: intParser, ...NUMERIC },
    ],
    [canEdit, cameras, cameraLabel]
  );

  const updateSelectedScene = (updater) => {
    setScenes((prev) => prev.map((s) => (s.sceneNo === selectedSceneNo ? updater(s) : s)));
    setScenesDirty(true);
  };

  const handleSaveScene = ({ sceneNo, sceneName }) => {
    if (sceneDialog.mode === 'add') {
      setScenes((prev) => [...prev, { sceneNo, sceneName, sortOrder: prev.length, steps: [] }]);
      setSelectedSceneNo(sceneNo);
    } else {
      const previousNo = sceneDialog.scene.sceneNo;
      setScenes((prev) => prev.map((s) => (s.sceneNo === previousNo ? { ...s, sceneNo, sceneName } : s)));
      setSelectedSceneNo(sceneNo);
    }
    setScenesDirty(true);
    setSceneDialog({ open: false, mode: 'add' });
  };

  const handleDeleteScene = () => {
    if (!selectedScene) return;
    if (!window.confirm(`"${selectedScene.sceneName}" 씬과 단계 ${selectedScene.steps.length}개를 삭제할까요?`)) return;
    setScenes((prev) => {
      const next = prev.filter((s) => s.sceneNo !== selectedSceneNo);
      setSelectedSceneNo(next[0]?.sceneNo ?? null);
      return next;
    });
    setScenesDirty(true);
  };

  const handleAddStep = () => {
    if (!selectedScene) return;
    if (cameras.length === 0) {
      alert('먼저 카메라를 추가하고 저장하세요.');
      return;
    }
    const last = selectedScene.steps[selectedScene.steps.length - 1];
    updateSelectedScene((scene) => ({
      ...scene,
      steps: [
        ...scene.steps,
        {
          _uid: nextUid(),
          camNo: last ? last.camNo : cameras[0].camNo,
          moveTime: last ? last.moveTime : 3,
          waitTime: last ? last.waitTime : 3,
          flr: last ? last.flr : '',
          grp: last ? last.grp : '',
          viewPoint: last ? last.viewPoint : 0,
        },
      ],
    }));
  };

  const handleDeleteSteps = () => {
    if (!selectedScene || selectedSteps.length === 0) return;
    const removing = new Set(selectedSteps.map((s) => s._uid));
    updateSelectedScene((scene) => ({ ...scene, steps: scene.steps.filter((s) => !removing.has(s._uid)) }));
    setSelectedSteps([]);
  };

  /** 선택한 단계 하나를 위/아래로 옮깁니다. 재생 순서가 곧 배열 순서입니다. */
  const handleMoveStep = (offset) => {
    if (!selectedScene || selectedSteps.length !== 1) return;
    const uid = selectedSteps[0]._uid;
    const index = selectedScene.steps.findIndex((s) => s._uid === uid);
    const target = index + offset;
    if (index < 0 || target < 0 || target >= selectedScene.steps.length) return;

    updateSelectedScene((scene) => {
      const steps = [...scene.steps];
      [steps[index], steps[target]] = [steps[target], steps[index]];
      return { ...scene, steps };
    });
  };

  const handleSaveScenes = async () => {
    try {
      await axiosPrivate.post('/view/scenes/save', {
        ...projectBody,
        presetId: selectedPresetId,
        scenes: scenes.map((scene, index) => ({
          sceneNo: scene.sceneNo,
          sceneName: scene.sceneName,
          sortOrder: index,
          steps: scene.steps.map(({ _uid, stepNo, ...rest }) => rest),
        })),
      });
      await fetchPresetContents(selectedPresetId);
      await fetchPresets(selectedPresetId);
      alert('씬을 저장했습니다.');
    } catch (err) {
      alert(apiMessage(err, '씬을 저장하지 못했습니다.'));
    }
  };

  /* ---------------------------------------------------------------- 렌더 */

  const cardBg =
    theme.palette.mode === 'dark'
      ? 'var(--mui-palette-background-default)'
      : 'var(--mui-palette-background-paper)';

  return (
    <Grid container spacing={3} sx={{ height: '100%' }}>
      <Grid size={{ xs: 12 }} sx={{ height: '100%' }}>
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 0, bgcolor: cardBg }}>
          <CardHeader
            title="카메라 / 씬 프리셋"
            subheader={`프로젝트: ${projectName ?? project_code} · 시스템: ${featureName}`}
            action={
              selectedPreset && (
                <Chip
                  label={`선택: ${selectedPreset.presetName}`}
                  color="primary"
                  variant="outlined"
                  sx={{ mt: 2, mr: 2 }}
                />
              )
            }
          />
          <Divider />

          {loadError && (
            <Alert severity="warning" sx={{ m: 4 }}>
              {loadError}
            </Alert>
          )}
          {!loadError && !canEdit && (
            <Alert severity="info" sx={{ m: 4 }}>
              읽기 권한만 있어 프리셋을 수정할 수 없습니다. "내 기본으로 지정"은 사용할 수 있습니다.
            </Alert>
          )}

          <CardContent sx={{ flexGrow: 1, height: 0, p: 0 }}>
            <Grid container sx={{ height: '100%' }}>
              {/* 좌측: 프리셋 목록 */}
              <Grid
                size={{ xs: 12, md: 3 }}
                sx={{ height: '100%', borderRight: `1px solid ${theme.palette.divider}`, pt: 3 }}
              >
                <PresetList
                  presets={presets}
                  selectedPresetId={selectedPresetId}
                  myDefaultPresetId={myDefaultPresetId}
                  canEdit={canEdit}
                  onSelect={handleSelectPreset}
                  onAdd={() => setPresetDialog({ open: true, mode: 'add' })}
                  onClone={() => setPresetDialog({ open: true, mode: 'add' })}
                  onEdit={() => setPresetDialog({ open: true, mode: 'edit' })}
                  onDelete={handleDeletePreset}
                  onSetProjectDefault={handleSetProjectDefault}
                  onSetMyDefault={handleSetMyDefault}
                />
              </Grid>

              {/* 우측: 카메라 / 씬 편집 */}
              <Grid size={{ xs: 12, md: 9 }} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {!selectedPreset ? (
                  <Box sx={{ p: 8, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      좌측에서 프리셋을 선택하거나 새로 만드세요.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Tabs value={tab} onChange={(_event, value) => setTab(value)} sx={{ px: 4 }}>
                      <Tab label={`카메라 (${cameras.length})`} />
                      <Tab label={`씬 (${scenes.length})`} />
                    </Tabs>
                    <Divider />

                    {tab === 0 && (
                      <>
                        <Stack direction="row" spacing={1} sx={{ p: 3, flexWrap: 'wrap', gap: 1 }}>
                          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={handleAddCamera} disabled={!canEdit}>
                            카메라 추가
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={handleDeleteCameras}
                            disabled={!canEdit || selectedCameras.length === 0}
                          >
                            선택 삭제
                          </Button>
                          <Box sx={{ flexGrow: 1 }} />
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<RestoreIcon />}
                            onClick={() => fetchPresetContents(selectedPresetId)}
                            disabled={!camerasDirty}
                          >
                            되돌리기
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<SaveIcon />}
                            onClick={handleSaveCameras}
                            disabled={!canEdit}
                          >
                            저장{camerasDirty ? ' *' : ''}
                          </Button>
                        </Stack>
                        <Divider />
                        <Box sx={{ flexGrow: 1, height: 0, px: 4, py: 2 }}>
                          <CommonAgGrid
                            gridRef={cameraGridRef}
                            rowData={cameras}
                            columnDefs={cameraColumnDefs}
                            customDefaultColDef={{ floatingFilter: false, filter: false }}
                            getRowId={(params) => params.data._uid}
                            onSelectionChanged={(event) =>
                              setSelectedCameras(event.api.getSelectedNodes().map((n) => n.data))
                            }
                            onCellValueChanged={() => setCamerasDirty(true)}
                          />
                        </Box>
                      </>
                    )}

                    {tab === 1 && (
                      <>
                        <Stack direction="row" spacing={1} sx={{ p: 3, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setSceneDialog({ open: true, mode: 'add' })} disabled={!canEdit}>
                            씬 추가
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => setSceneDialog({ open: true, mode: 'edit', scene: selectedScene })}
                            disabled={!canEdit || !selectedScene}
                          >
                            씬 이름
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={handleDeleteScene}
                            disabled={!canEdit || !selectedScene}
                          >
                            씬 삭제
                          </Button>
                          <Box sx={{ flexGrow: 1 }} />
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<RestoreIcon />}
                            onClick={() => fetchPresetContents(selectedPresetId)}
                            disabled={!scenesDirty}
                          >
                            되돌리기
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<SaveIcon />}
                            onClick={handleSaveScenes}
                            disabled={!canEdit}
                          >
                            저장{scenesDirty ? ' *' : ''}
                          </Button>
                        </Stack>

                        {scenes.length > 0 && (
                          <Tabs
                            value={selectedSceneNo ?? false}
                            onChange={(_event, value) => {
                              setSelectedSceneNo(value);
                              setSelectedSteps([]);
                            }}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{ px: 4, borderTop: `1px solid ${theme.palette.divider}` }}
                          >
                            {scenes.map((scene) => (
                              <Tab
                                key={scene.sceneNo}
                                value={scene.sceneNo}
                                label={`${scene.sceneNo}. ${scene.sceneName} (${scene.steps.length})`}
                              />
                            ))}
                          </Tabs>
                        )}
                        <Divider />

                        {!selectedScene ? (
                          <Box sx={{ p: 8, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              씬이 없습니다. "씬 추가"로 만드세요.
                            </Typography>
                          </Box>
                        ) : (
                          <>
                            <Stack direction="row" spacing={1} sx={{ px: 4, py: 2, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleAddStep} disabled={!canEdit}>
                                단계 추가
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={handleDeleteSteps}
                                disabled={!canEdit || selectedSteps.length === 0}
                              >
                                단계 삭제
                              </Button>
                              <Tooltip title="재생 순서를 위로 옮깁니다 (한 개만 선택)">
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<ArrowUpwardIcon />}
                                    onClick={() => handleMoveStep(-1)}
                                    disabled={!canEdit || selectedSteps.length !== 1}
                                  >
                                    위로
                                  </Button>
                                </span>
                              </Tooltip>
                              <Tooltip title="재생 순서를 아래로 옮깁니다 (한 개만 선택)">
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<ArrowDownwardIcon />}
                                    onClick={() => handleMoveStep(1)}
                                    disabled={!canEdit || selectedSteps.length !== 1}
                                  >
                                    아래로
                                  </Button>
                                </span>
                              </Tooltip>
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                위에서 아래 순서대로 카메라가 전환됩니다.
                              </Typography>
                            </Stack>
                            <Box sx={{ flexGrow: 1, height: 0, px: 4, pb: 2 }}>
                              <CommonAgGrid
                                gridRef={stepGridRef}
                                rowData={selectedScene.steps}
                                columnDefs={stepColumnDefs}
                                customDefaultColDef={{ sortable: false, filter: false, floatingFilter: false }}
                                getRowId={(params) => params.data._uid}
                                onSelectionChanged={(event) =>
                                  setSelectedSteps(event.api.getSelectedNodes().map((n) => n.data))
                                }
                                onCellValueChanged={() => setScenesDirty(true)}
                              />
                            </Box>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <PresetDialog
        open={presetDialog.open}
        mode={presetDialog.mode}
        preset={selectedPreset}
        presets={presets}
        onClose={() => setPresetDialog({ open: false, mode: 'add' })}
        onSave={handleSavePreset}
      />

      <SceneDialog
        open={sceneDialog.open}
        mode={sceneDialog.mode}
        scene={sceneDialog.scene}
        usedSceneNos={scenes.map((s) => s.sceneNo)}
        onClose={() => setSceneDialog({ open: false, mode: 'add' })}
        onSave={handleSaveScene}
      />
    </Grid>
  );
};

export default ViewPreset;
